import type { BasicPassFragment } from '@drycut/graphql';

import { PassPayment } from '@drycut/controlled-components';
import { ActionButtons, Button, FormHelpText, Heading, Stack } from '@drycut/ui-components';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { ControlledNameField } from '../ControlledNameField';
import { ControlledPhoneField } from '../ControlledPhoneField';
import PassPrice from './PassPrice';

type Props = {
  manual?: boolean;
  pass: BasicPassFragment;
  storeSlug?: string;
};

type PassPurchaseValues = {
  name: string;
  phone: string;
};

export function PassPurchaseForm({ storeSlug, pass, manual = false }: Props): React.ReactElement {
  const { title, subtitle, price } = pass;
  const formMethods = useForm<PassPurchaseValues>({
    defaultValues: {
      name: '',
      phone: '',
    },
  });

  const customerName = useWatch({ control: formMethods.control, name: 'name' });
  const phoneNumber = useWatch({ control: formMethods.control, name: 'phone' });

  return (
    <FormProvider {...formMethods}>
      <Stack gap={5} className="items-center text-center h-full">
        <Stack>
          <Heading variant="h2">{title}</Heading>
          {subtitle && <Heading variant="h6">{subtitle}</Heading>}
        </Stack>

        <PassPrice pass={pass} className="my-5" />

        <Stack
          as="fieldset"
          gap={4}
          className="w-72"
          disabled={formMethods.formState.isValidating || formMethods.formState.isSubmitting}
        >
          <ControlledNameField required />

          <div>
            <ControlledPhoneField autoComplete={manual ? 'off' : 'phone-number'} required />
            <FormHelpText className="text-zinc-400 font-aribau">
              Wir schicken Dir Deinen Code an diese Nummer.
            </FormHelpText>
          </div>
        </Stack>

        <ActionButtons className="mt-4">
          <PassPayment
            formMethods={formMethods}
            amount={price}
            data={{
              manual,
              customerName,
              phoneNumber,
              passGid: pass.id,
              storeSlug: storeSlug,
            }}
          />
          <Button as="a" href={manual ? `/${storeSlug}` : '/passes'} fill="outlined">
            Cancel
          </Button>
        </ActionButtons>
      </Stack>
    </FormProvider>
  );
}
